import type { ThreeElements } from '@react-three/fiber'
import { useDetalhesFinos, useNivelQualidade } from '../ambiente/contextoQualidade'
import { PIVO_BRACO } from './geometriaAlargador'
import {
  ESPESSURA_BRACO,
  ESPESSURA_BOLSA,
  ESPESSURA_PLACA_GARFO,
  malhasAlargador,
  type MalhasAlargador,
} from './malhasAlargador'

interface Props extends Omit<ThreeElements['group'], 'children'> {
  /**
   * Abertura de cada braço em radianos, a partir do alargador fechado.
   * Em 0 as faces internas dos braços ficam encostadas.
   */
  anguloMandibula?: number
  /**
   * Destaca o equipamento quando ativo.
   */
  ativa?: boolean
}

const LARANJA = '#ea580c'
const LARANJA_ATIVO = '#f97316'
const ACO_FORJADO = '#c9d1d9'
const ACO_USINADO = '#8b97a6'
const GRAFITE = '#1f1f23'
const PRETO_GARFO = '#15181d'
const PRETO_MODULO = '#16191e'
const ACO_PARAFUSO = '#cbd5e1'
const CROMO = '#f1f5f9'

/** Face externa da placa do garfo, a partir do plano médio da ferramenta. */
const Z_PLACA = ESPESSURA_BRACO / 2 + 0.005 + ESPESSURA_PLACA_GARFO / 2 + 0.002
const Z_FACE_PLACA = Z_PLACA + ESPESSURA_PLACA_GARFO / 2

/**
 * Braço superior. O contorno já vem desenhado na pose fechada (ver
 * `geometriaAlargador`), então o braço só gira em torno do pivô pela
 * abertura: em 0 ele fica exatamente como desenhado, encostado no outro.
 */
function Braco({ malhas, abertura, detalhes }: { malhas: MalhasAlargador; abertura: number; detalhes: boolean }) {
  const [px, py] = PIVO_BRACO
  return (
    <group position={[px, py, 0]} rotation={[0, 0, abertura]}>
      <group position={[-px, -py, 0]}>
        {/* Corpo forjado, com o dorso em curva e o calcanhar em volta do pivô */}
        <mesh geometry={malhas.braco} castShadow>
          <meshStandardMaterial color={ACO_FORJADO} metalness={0.85} roughness={0.3} />
        </mesh>

        {/* Bolsas usinadas de alívio de peso, uma em cada face */}
        {detalhes &&
          [1, -1].map((lado) => (
            <mesh key={lado} geometry={malhas.bolsa} position={[0, 0, lado * (ESPESSURA_BRACO / 2 + ESPESSURA_BOLSA / 2 - 0.001)]}>
              <meshStandardMaterial color={ACO_USINADO} metalness={0.9} roughness={0.38} />
            </mesh>
          ))}

        {/* Ponteira dentada destacável, mais grossa que o braço */}
        <mesh geometry={malhas.ponteira} castShadow>
          <meshStandardMaterial color={GRAFITE} metalness={0.55} roughness={0.45} />
        </mesh>

        {/* Pino de engate rápido da ponteira, aparente nas duas faces */}
        {detalhes &&
          [1, -1].map((lado) => (
            <mesh key={lado} position={[0.44, 0.024, lado * 0.0275]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.009, 0.009, 0.004, 16]} />
              <meshStandardMaterial color={ACO_PARAFUSO} metalness={0.9} roughness={0.2} />
            </mesh>
          ))}
      </group>
    </group>
  )
}

/**
 * Alargador hidráulico a bateria, inspirado no Holmatro série Pentheon.
 * Não é cópia: guarda a silhueta e as cores (laranja e preto), sem logo nem texto.
 *
 * - Braços forjados em "bico de pato", desenhados fechados, com bolsas
 *   usinadas e ponteiras dentadas destacáveis;
 * - garfo de duas placas laterais envolvendo os pivôs, com porcas sextavadas;
 * - haste cromada do pistão entre o garfo e o cilindro;
 * - alça de transporte 360° com LEDs de iluminação do corte;
 * - cilindro hidráulico laranja torneado, com sulco de vedação;
 * - módulo traseiro com aletas, anel de comando giratório e manopla com anéis.
 *
 * No nível de qualidade `baixa` as peças miúdas saem e as curvas ficam mais
 * grossas; a silhueta e o contrato cinemático são os mesmos em todos os níveis.
 */
export function AlargadorHolmatro3D({
  anguloMandibula = 0,
  ativa = false,
  ...props
}: Props) {
  const nivel = useNivelQualidade()
  const detalhes = useDetalhesFinos()
  const malhas = malhasAlargador(nivel)
  const [px, py] = PIVO_BRACO

  return (
    <group {...props}>
      {/* 1. Braços articulados. O inferior é o espelho do superior em y: o
          espelho inverte o sentido da rotação, então a mesma abertura afasta
          os dois braços do eixo. */}
      <Braco malhas={malhas} abertura={anguloMandibula} detalhes={detalhes} />
      <group scale={[1, -1, 1]}>
        <Braco malhas={malhas} abertura={anguloMandibula} detalhes={detalhes} />
      </group>

      {/* 2. Garfo: duas placas laterais com os braços entre elas */}
      {[1, -1].map((lado) => (
        <mesh key={lado} geometry={malhas.placaGarfo} position={[0, 0, lado * Z_PLACA]} castShadow>
          <meshStandardMaterial color={PRETO_GARFO} metalness={0.45} roughness={0.5} />
        </mesh>
      ))}

      {/* Pinos dos pivôs atravessando garfo e braços; porca sextavada em cada face */}
      {[py, -py].map((y) => (
        <group key={y} position={[px, y, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.02, 0.02, Z_FACE_PLACA * 2 + 0.006, 20]} />
            <meshStandardMaterial color="#334155" metalness={0.8} roughness={0.32} />
          </mesh>
          {detalhes &&
            [1, -1].map((lado) => (
              <mesh key={lado} position={[0, 0, lado * (Z_FACE_PLACA + 0.004)]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.027, 0.027, 0.008, 6]} />
                <meshStandardMaterial color={ACO_PARAFUSO} metalness={0.85} roughness={0.28} />
              </mesh>
            ))}
        </group>
      ))}

      {/* Travessa traseira do garfo, onde a haste do pistão se apoia */}
      <mesh position={[-0.06, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.024, 0.024, Z_FACE_PLACA * 2, 20]} />
        <meshStandardMaterial color="#334155" metalness={0.8} roughness={0.3} />
      </mesh>

      {/* Haste cromada do pistão, do cilindro à travessa */}
      <mesh position={[-0.09, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.022, 0.022, 0.07, 24]} />
        <meshStandardMaterial color={CROMO} metalness={0.95} roughness={0.12} />
      </mesh>

      {/* 3. Alça de transporte 360° com LEDs de iluminação do corte */}
      <group position={[-0.1, 0, 0]}>
        <mesh rotation={[0, Math.PI / 2, 0]} castShadow>
          <torusGeometry args={[0.11, 0.016, nivel === 'baixa' ? 8 : 14, nivel === 'baixa' ? 24 : 40]} />
          <meshStandardMaterial color="#1c2430" roughness={0.6} metalness={0.3} />
        </mesh>
        {[1, -1].map((lado) => (
          <mesh key={lado} position={[0, lado * 0.075, 0]} rotation={[0, 0, lado * 0.35]}>
            <cylinderGeometry args={[0.013, 0.013, 0.07, 12]} />
            <meshStandardMaterial color="#0f172a" roughness={0.5} />
          </mesh>
        ))}
        {detalhes &&
          [1, -1].map((lado) => (
            <mesh key={lado} position={[0.016, lado * 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.008, 0.008, 0.006, 12]} />
              <meshStandardMaterial color="#f8fafc" emissive="#ffffff" emissiveIntensity={0.8} />
            </mesh>
          ))}
      </group>

      {/* 4. Cilindro hidráulico laranja */}
      <mesh geometry={malhas.cilindro} castShadow>
        <meshStandardMaterial color={ativa ? LARANJA_ATIVO : LARANJA} metalness={0.25} roughness={0.42} />
      </mesh>

      {/* 5. Módulo traseiro: motor aletado, anel de comando e manopla */}
      <mesh geometry={malhas.moduloTraseiro} castShadow>
        <meshStandardMaterial color={PRETO_MODULO} metalness={0.35} roughness={0.55} />
      </mesh>

      {/* Indicador de carga da bateria, sobre o motor */}
      {detalhes && (
        <group position={[-0.5, 0.081, 0]}>
          <mesh>
            <boxGeometry args={[0.046, 0.004, 0.024]} />
            <meshStandardMaterial color="#09090b" roughness={0.6} />
          </mesh>
          {[-0.015, -0.005, 0.005, 0.015].map((x) => (
            <mesh key={x} position={[x, 0.0025, 0]}>
              <boxGeometry args={[0.006, 0.001, 0.012]} />
              <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.9} />
            </mesh>
          ))}
        </group>
      )}
    </group>
  )
}
